// GameAnalyticsProvider: sends events to GameAnalytics via its HTML5 SDK.
// The SDK script is lazily injected (kept out of the core bundle) and works in
// both web browsers and the Capacitor Android WebView. Events fired before the
// SDK is ready are buffered and flushed on init. Everything is guarded — a
// missing key, blocked script, or SDK error never throws into the game.
//
// Platform separation:
//  - Provide separate GA games per platform via `android` / `ios` key sets; the
//    provider auto-selects by platform (this gives fully separate streams).
//  - The GA "build" is tagged "<platform>-<version>" so even a single GA game
//    can be filtered into web vs android in the dashboard.

const DEFAULT_SCRIPT = 'https://download.gameanalytics.com/js/GameAnalytics-4.6.0.min.js';
const SCRIPT_TIMEOUT_MS = 8000;
const MAX_QUEUE = 200;

let scriptPromise = null;

function hasDOM() {
  return typeof window !== 'undefined' && typeof document !== 'undefined' && !!document.createElement;
}

function loadScript(url, log) {
  if (!hasDOM()) return Promise.resolve(false);
  if (typeof window.GameAnalytics === 'function') return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const timer = setTimeout(() => { log.warn('[analytics:ga] script load timed out'); resolve(false); }, SCRIPT_TIMEOUT_MS);
    const s = document.createElement('script');
    s.async = true;
    s.src = url;
    s.onload = () => { clearTimeout(timer); resolve(typeof window.GameAnalytics === 'function'); };
    s.onerror = () => { clearTimeout(timer); log.warn('[analytics:ga] script load failed'); resolve(false); };
    (document.head || document.body || document.documentElement).appendChild(s);
  });
  return scriptPromise;
}

// GA design-event ids allow letters/digits/space and - _ . ( ) ! ? with ':' as
// a hierarchy separator. Coerce anything else and cap length.
function sanitizeId(name) {
  return String(name).replace(/[^A-Za-z0-9\s\-_.()!?:]/g, '_').slice(0, 64) || 'event';
}

export class GameAnalyticsProvider {
  constructor(cfg, ctx = {}) {
    this.cfg = cfg || {};
    this.log = ctx.log || console;
    this.appId = ctx.appId || 'game';
    this.platform = ctx.platform || 'web';
    this.version = ctx.version || '1.0.0';
    this._ready = false;
    this._queue = [];
    this._warnedSend = false;   // warn once, then stay quiet
  }

  _keys() {
    const perPlatform = this.cfg[this.platform]; // cfg.android / cfg.ios
    if (perPlatform?.gameKey && perPlatform?.secretKey) return perPlatform;
    return { gameKey: this.cfg.gameKey, secretKey: this.cfg.secretKey };
  }

  async init() {
    const { gameKey, secretKey } = this._keys();
    if (!gameKey || !secretKey) {
      this.log.warn(`[analytics:ga] missing gameKey/secretKey for platform "${this.platform}"`);
      return { ok: false, reason: 'not-configured' };
    }
    const ok = await loadScript(this.cfg.scriptUrl || DEFAULT_SCRIPT, this.log);
    if (!ok || typeof window.GameAnalytics !== 'function') {
      this.log.warn('[analytics:ga] SDK unavailable; events buffered then dropped quietly');
      return { ok: false, reason: 'script-unavailable' };
    }
    try {
      const GA = window.GameAnalytics;
      // Tag the build with platform so web vs android are distinguishable.
      GA('configureBuild', `${this.platform}-${this.version}`.slice(0, 32));
      GA('initialize', gameKey, secretKey);
      this._ready = true;
      this.log.debug(`[analytics:ga] initialized stream "${this.appId}"`);
      const queued = this._queue.splice(0);
      for (const e of queued) this._send(e.name, e.props);
      return { ok: true };
    } catch (err) {
      this.log.warn('[analytics:ga] initialize failed', err);
      return { ok: false, reason: 'error' };
    }
  }

  event(name, props) {
    if (!this._ready) {
      if (this._queue.length < MAX_QUEUE) this._queue.push({ name, props });
      return;
    }
    this._send(name, props);
  }

  _send(name, props) {
    try {
      const GA = window.GameAnalytics;
      const id = sanitizeId(name);
      // GA design events carry an optional numeric value.
      const value = typeof props?.value === 'number' ? props.value : undefined;
      if (value !== undefined) GA('addDesignEvent', id, value);
      else GA('addDesignEvent', id);
    } catch (err) {
      // Warn once, then stay quiet so a broken SDK doesn't spam the console.
      if (!this._warnedSend) {
        this._warnedSend = true;
        this.log.warn('[analytics:ga] event failed; further failures will be silent', err);
      }
    }
  }
}
